/**
 * ReceiptImportSheet — การ์ดใบเสร็จที่เด้งขึ้นเมื่อลูกค้าเปิดแอปจากลิงก์ใบเสร็จ
 *
 * ลูกค้าสแกน QR บนใบเสร็จ → หน้า /r/ → กด "เปิดในแอป" → แอปเปิด → การ์ดนี้
 * ให้กดบันทึกเป็นรายจ่ายเอง ไม่บันทึกอัตโนมัติ — คนที่แค่อยากดู
 * จะได้ไม่มีตัวเลขโผล่ในบัญชีโดยไม่ได้ตั้งใจ
 *
 * 🔴 เรนเดอร์อยู่ใน .app ไม่ใช่ portal ออกไปที่ document.body
 *    portal ที่อยู่นอก .app ได้โทเคนสีธีมมืดเสมอแม้ผู้ใช้อยู่ธีมสว่าง
 *    (บั๊กที่เคยทำให้ปุ่มกลายเป็นขาวบนขาว 1.01:1 — ดู moneyma-theme-contrast)
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  RECEIPT_LINK_EVENT,
  peekReceiptToShow,
  clearReceiptToShow,
  fetchPublicReceipt,
  isReceiptImported,
  markReceiptImported,
  receiptToTransaction,
} from '../services/ReceiptLoopService';
import './ReceiptImportSheet.css';

const money = (n) =>
  `฿${(Number(n) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function formatWhen(iso, language) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(language === 'th' ? 'th-TH' : 'en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function ReceiptImportSheet({ t, language, onSave }) {
  const [token, setToken] = useState(() => peekReceiptToShow());
  const [status, setStatus] = useState('idle'); // loading | ready | saved | already | not_found | offline | unavailable
  const [receipt, setReceipt] = useState(null);
  const latestToken = useRef(token);
  const saving = useRef(false);

  // แอปเปิดค้างอยู่แล้วแล้วมีลิงก์ใหม่เข้ามา
  useEffect(() => {
    const onLink = (e) => setToken((e && e.detail && e.detail.token) || peekReceiptToShow());
    window.addEventListener(RECEIPT_LINK_EVENT, onLink);
    return () => window.removeEventListener(RECEIPT_LINK_EVENT, onLink);
  }, []);

  const load = useCallback(async (tok) => {
    latestToken.current = tok;
    saving.current = false;
    setStatus('loading');
    setReceipt(null);
    const res = await fetchPublicReceipt(tok);
    if (latestToken.current !== tok) return; // มีลิงก์ใหม่เข้ามาระหว่างรอ ทิ้งผลเก่า
    if (!res.ok) {
      setStatus(res.reason);
      return;
    }
    setReceipt(res.receipt);
    setStatus(isReceiptImported(tok) ? 'already' : 'ready');
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  if (!token) return null;

  const close = () => {
    clearReceiptToShow();
    latestToken.current = null;
    setToken(null);
    setStatus('idle');
    setReceipt(null);
  };

  const save = () => {
    // กันกดรัว — setState ยังไม่ทันเรนเดอร์ สองครั้งจะเห็น 'ready' ทั้งคู่
    if (saving.current || status !== 'ready' || !receipt) return;
    saving.current = true;
    markReceiptImported(token);
    onSave(receiptToTransaction(receipt, (shop) =>
      String(t.receiptImportDesc).replace('{shop}', shop || t.receiptImportUnknownShop)));
    clearReceiptToShow();
    setStatus('saved');
  };

  const isError = status === 'not_found' || status === 'offline' || status === 'unavailable';
  const errorText = {
    not_found: t.receiptImportNotFound,
    offline: t.receiptImportOffline,
    unavailable: t.receiptImportUnavailable,
  }[status];

  return (
    <div className="mm-rx-backdrop" role="presentation" onClick={close}>
      <div
        className="mm-rx-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mm-rx-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mm-rx-head">
          <h2 id="mm-rx-title">{t.receiptImportTitle}</h2>
          <button className="mm-rx-x" onClick={close} aria-label={t.receiptImportClose}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>
          </button>
        </div>

        {status === 'loading' && <p className="mm-rx-muted">{t.receiptImportLoading}</p>}

        {isError && (
          <>
            {/* โค้ดเหตุผลท้ายประโยค — ภาพหน้าจอเดียวก็รู้ว่าติดตรงไหน (กฎเดียวกับ [configure_timeout]) */}
            <p className="mm-rx-muted">{errorText} [{status}]</p>
            <div className="mm-rx-actions">
              <button className="mm-rx-btn mm-rx-btn-ghost" onClick={close}>{t.receiptImportClose}</button>
              {status !== 'not_found' && (
                <button className="mm-rx-btn mm-rx-btn-primary" onClick={() => load(token)}>{t.receiptImportRetry}</button>
              )}
            </div>
          </>
        )}

        {receipt && !isError && status !== 'loading' && (
          <>
            <div className="mm-rx-body">
              <div className="mm-rx-shop">{receipt.shopName || t.receiptImportUnknownShop}</div>
              <div className="mm-rx-when">{formatWhen(receipt.issuedAt, language)}</div>
              <div className="mm-rx-total">{money(receipt.total)}</div>
              <div className="mm-rx-meta">
                {String(t.receiptImportItems).replace('{n}', receipt.itemCount)}
                {receipt.docNo ? ` · ${receipt.docNo}` : ''}
              </div>
            </div>

            {status === 'ready' && <p className="mm-rx-hint">{t.receiptImportHint}</p>}
            {status === 'saved' && <p className="mm-rx-ok">{t.receiptImportSaved}</p>}
            {status === 'already' && <p className="mm-rx-ok">{t.receiptImportAlready}</p>}

            <div className="mm-rx-actions">
              <button className="mm-rx-btn mm-rx-btn-ghost" onClick={close}>{t.receiptImportClose}</button>
              {status === 'ready' && (
                <button className="mm-rx-btn mm-rx-btn-primary" onClick={save}>{t.receiptImportSave}</button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
